/**
 * lib/convexHull.js — Phase E v8c.5 (2026-02-28).
 *
 * Andrew's Monotone Chain convex hull + shoelace polygon area for
 * the calibration coverage-score metric. Pure helpers, no React /
 * canvas dependencies — Jest-testable in isolation.
 *
 * Use-case: collect every world-coord the wizard has calibrated
 * across all zones (area corners + line endpoints + single points),
 * compute their convex hull, divide its area by the full pitch area
 * (e.g. 1600 m² for U17) → coverage percentage.
 */

/**
 * Compute the convex hull of a 2-D point cloud (Monotone Chain).
 *
 * @param {Array<[number, number]>} points - input coords [x, y]
 * @returns {Array<[number, number]>} hull vertices in CCW order,
 *   first and last vertex NOT duplicated. Returns the input itself
 *   when ≤2 distinct points. Returns [] for empty input.
 */
export function convexHull(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  // Deduplicate + sort lex (x ASC, then y ASC).
  const dedup = Array.from(
    new Map(points.map((p) => [`${p[0]},${p[1]}`, [+p[0], +p[1]]])).values()
  );
  if (dedup.length < 2) return dedup;
  dedup.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (dedup.length === 2) return dedup;

  const cross = (O, A, B) =>
    (A[0] - O[0]) * (B[1] - O[1]) - (A[1] - O[1]) * (B[0] - O[0]);

  // Lower hull.
  const lower = [];
  for (const p of dedup) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  // Upper hull.
  const upper = [];
  for (let i = dedup.length - 1; i >= 0; i--) {
    const p = dedup[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Shoelace area of a simple (non-self-intersecting) polygon.
 *
 * @param {Array<[number, number]>} polygon - vertices in order
 *   (CW or CCW, doesn't matter — abs() at the end).
 * @returns {number} area in the same units² as the input.
 */
export function polygonArea(polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [x1, y1] = polygon[i];
    const [x2, y2] = polygon[(i + 1) % polygon.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

/**
 * Coverage score: convex-hull-area / total-pitch-area, clamped to [0, 1].
 *
 * @param {Array<[number, number]>} worldPoints - every calibrated
 *   world coord (area corners, line endpoints, single points).
 * @param {number} fieldLengthM - canonical pitch length in metres
 *   (64 for U17, 100 for Adult, etc.).
 * @param {number} fieldWidthM - canonical pitch width in metres.
 * @returns {{ score: number, hullAreaM2: number, fieldAreaM2: number,
 *             hullVertices: Array<[number, number]> }}
 */
export function coverageScore(worldPoints, fieldLengthM, fieldWidthM) {
  const fieldArea = Math.max(1, fieldLengthM * fieldWidthM);
  const hull = convexHull(worldPoints || []);
  const hullArea = polygonArea(hull);
  const score = Math.min(1, Math.max(0, hullArea / fieldArea));
  return {
    score,
    hullAreaM2: hullArea,
    fieldAreaM2: fieldArea,
    hullVertices: hull,
  };
}

/**
 * Tail-side helper for the Sanity panel — picks a Tailwind text-color
 * class based on the v8c.5 grading band:
 *   < 40% → red, 40–70% → orange, > 70% → green.
 */
export function coverageColorClass(score) {
  if (score > 0.7) return "text-emerald-400";
  if (score >= 0.4) return "text-amber-400";
  return "text-rose-400";
}
